import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import * as cadviewer from "cadviewer"; // 9.27.3
import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import useConfig from "../../demos/hooks/useConfig";
import { useLocalStorage } from "../../utils/useLocalStorage";
import DisabledClickWrapper from "../DisabledClickWrapper";
import EmployeesListRightPanel from "../EmployeesListRightPanel";
import FolderListRightPanel from "../FolderListRightPanel";
import LoginForm from "../LoginForm/LoginForm.Component";
import PlanJsonRightPanel from "../PlanJsonRightPanel";
import QuickPrintPdf from "../QuickPrintPdf";
import RegisterForm from "../RegisterForm/RegisterForm.Component";
import Tooltip from "../Tooltip";

// Définit un type pour la taille de la fenêtre
interface WindowSize {
  width: number | undefined;
  height: number | undefined;
}

/**
 * Hook personnalisé pour suivre la taille de la fenêtre du navigateur.
 * @returns {WindowSize} Un objet contenant la largeur et la hauteur actuelles de la fenêtre.
 * Initialise la largeur et la hauteur à `undefined` pour éviter les problèmes d'hydratation SSR.
 */
export const useWindowSize = (): WindowSize => {
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    // Fonction handler appelée lors du redimensionnement de la fenêtre
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    // Ajoute l'écouteur d'événement
    window.addEventListener("resize", handleResize);

    // Appelle le handler immédiatement pour définir la taille initiale
    handleResize();

    // Nettoyage : supprime l'écouteur d'événement lorsque le composant est démonté
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Le tableau vide assure que l'effet ne s'exécute qu'au montage et au démontage

  return windowSize;
};

// hooks for check if we are on the tablet or mobile based on the window width
export const isTabletOrMobile = (width: number | undefined) => {
  return width && width < 1024;
};

const RightAction = () => {
  const { width } = useWindowSize();
  const { config } = useConfig();
  const isMobile = useMemo(() => isTabletOrMobile(width), [width]);
  const [rightFolderPanelOpen, setRightFolderPanelOpen] = useState(false);
  const [rightEmployeePanelOpen, setRightEmployeePanelOpen] = useState(false);
  const [rightPlansPanelOpen, setRightPlansPanelOpen] = useState(false);
  const [quickPdfPrintOpen, setQuickPdfPrintOpen] = useState(false);
  const {
    user,
    setUser,
    setSelectedSpaceObjectID,
    selectedPlan,
    currentCanvasInstance,
    disableFeature,
    isDataImportActive,
    setIsDataImportActive,
    setCopiedSpaceId,
  } = useContext(CadviewerContext);
  const { getItem } = useLocalStorage();
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // 9.66.5
  const cvjs_customCommand_19 = () => {
    setRightFolderPanelOpen(true);
    cadviewer.cvjs_hideOnlyPop();
  };

  useEffect(() => {
    cadviewer.cvjs_setCallbackMethod(
      "cvjs_customCommand_19",
      cvjs_customCommand_19
    );
  }, []);

  useEffect(() => {
    if (!mounted) {
      setUser(
        getItem("auth_user") ? JSON.parse(getItem("auth_user") as string) : null
      );
      setMounted(true);
    }
  }, [getItem, setUser, mounted]);

  useEffect(() => {
    if (user?.token) {
      // 	then we can get user profile
      fetch(`${config.ServerBackEndUrl}/auth/profile`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            setUser({
              ...user,
              ...data.user,
            });
          }
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    }
  }, []);

  let rightMenuMode = !isMobile ? config.rightMenuMode : "Popup";

  return (
    <div className="top-16 flex items-start justify-start lg:justify-end flex-wrap flex-1">
      {config.enableLoginFeature &&
        (!user ? (
          <>
            <LoginForm />
            <RegisterForm />
          </>
        ) : (
          <Menu
            as="div"
            className="relative mr-2 inline-block z-50"
            style={{ zIndex: 20000 }}
          >
            <div>
              <Menu.Button className="relative flex  text-sm focus:outline-none items-center">
                <span className="absolute -inset-1.5" />
                <span className="sr-only">Open user menu</span>
                <img
                  className="h-10 w-10 rounded-full bg-gray-800"
                  src={user?.avatar_url || "/avatar.jpg"}
                  alt=""
                />
                <span className="hidden lg:flex lg:items-center">
                  <span
                    className="ml-4 text-sm font-semibold leading-6 text-gray-900"
                    aria-hidden="true"
                  >
                    {user.username}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                    />
                  </svg>
                </span>
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute left-0 right-auto md:left-auto md:right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/profile"
                      className={
                        (active ? "bg-gray-100 " : "") +
                        "block px-4 py-2 text-sm text-gray-700"
                      }
                    >
                      My Profile
                    </Link>
                  )}
                </Menu.Item>
                {user.role === "admin" && (
                  <Menu.Item>
                    {({ active }) => (
                      <a
                        href="/users"
                        className={
                          (active ? "bg-gray-100 " : "") +
                          "block px-4 py-2 text-sm text-gray-700"
                        }
                      >
                        Users
                      </a>
                    )}
                  </Menu.Item>
                )}
                {user.role === "admin" && (
                  <Menu.Item>
                    {({ active }) => (
                      <a
                        href="/settings"
                        className={
                          (active ? "bg-gray-100 " : "") +
                          "block px-4 py-2 text-sm text-gray-700"
                        }
                      >
                        Settings
                      </a>
                    )}
                  </Menu.Item>
                )}
                <Menu.Item>
                  {({ active }) => (
                    <span
                      onClick={() => setUser(null)}
                      className={
                        (active ? "bg-gray-100 " : "") +
                        "block px-4 py-2 text-sm text-gray-700"
                      }
                    >
                      Sign out
                    </span>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        ))}
      {rightMenuMode === "Inline" && (
        <div className="flex flex-grow-1 gap-1 flex-wrap justify-end">
          {config.enableQuickPdfPrint && selectedPlan && (
            <DisabledClickWrapper>
              <button
                onClick={() => {
                  setQuickPdfPrintOpen(true);
                }}
                disabled={!selectedPlan || disableFeature}
                className={`
						${
              !selectedPlan
                ? "bg-[#e5e7eb] text-[#727272] hover:bg-[#e5e7eb]"
                : " text-white bg-btn-primary hover:bg-btn-primary-hover focus-visible:outline-btn-primary-ring"
            }
						inline-flex items-center gap-x-1.5 rounded-md  px-2.5 py-2 text-sm font-semibold shadow-sm  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2  mr-2
						${
              config.helperMethodsActionSize === "small"
                ? " text-xs my-1 mr-1 py-[2px] px-1"
                : config.helperMethodsActionSize === "large"
                ? " text-base my-2 mr-2 py-2 px-3"
                : " text-sm my-2 mr-2 py-1 px-3"
            }
						disabled:bg-gray-300 disabled:text-white
					`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5Zm-3 0h.008v.008H15V10.5Z"
                  />
                </svg>
                <span>{config.enableQuickPdfPrintText}</span>
              </button>
            </DisabledClickWrapper>
          )}
          {config.enableRightJSONModalPanel && (
            <DisabledClickWrapper>
              <button
                onClick={() => {
                  if (location.pathname === "/profile") {
                    navigate("/");
                  }
                  cadviewer.cvjs_hideOnlyPop();
                  setSelectedSpaceObjectID(undefined);

                  // 10.44.3
                  //cadviewer.cvjs_ClearDrawing(currentCanvasInstance as string);

                  setRightPlansPanelOpen(true);
                }}
                // here select property

                className={`inline-flex items-center gap-x-1.5 rounded-md bg-btn-primary px-2.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-btn-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-btn-primary-ring mr-2
										${
                      config.helperMethodsActionSize === "small"
                        ? " text-xs my-1 mr-1 py-[2px] px-1"
                        : config.helperMethodsActionSize === "large"
                        ? " text-base my-2 mr-2 py-2 px-3"
                        : " text-sm my-2 mr-2 py-1 px-3"
                    }
										disabled:bg-gray-300 disabled:text-white
										`}
                //									className={`bg-primary-500 text-white group flex items-center rounded-md px-2 py-2 text-sm  flex-grow-0 flex-shrink-0`}
                disabled={disableFeature}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 mr-3"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m7.875 14.25 1.214 1.942a2.25 2.25 0 0 0 1.908 1.058h2.006c.776 0 1.497-.4 1.908-1.058l1.214-1.942M2.41 9h4.636a2.25 2.25 0 0 1 1.872 1.002l.164.246a2.25 2.25 0 0 0 1.872 1.002h2.092a2.25 2.25 0 0 0 1.872-1.002l.164-.246A2.25 2.25 0 0 1 16.954 9h4.636M2.41 9a2.25 2.25 0 0 0-.16.832V12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 12V9.832c0-.287-.055-.57-.16-.832M2.41 9a2.25 2.25 0 0 1 .382-.632l3.285-3.832a2.25 2.25 0 0 1 1.708-.786h8.43c.657 0 1.281.287 1.709.786l3.284 3.832c.163.19.291.404.382.632M4.5 20.25h15A2.25 2.25 0 0 0 21.75 18v-2.625c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125V18a2.25 2.25 0 0 0 2.25 2.25Z"
                  />
                </svg>
                <span>{config.enableRightJSONModalPanelText}</span>
              </button>
            </DisabledClickWrapper>
          )}
          {config.enableHelpButton1 && (
            <button
              onClick={() => {
                window.open(config.helpbutton1link, "_blank");
              }}
              className={`inline-flex items-center gap-x-1.5 rounded-md bg-btn-primary px-2.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-btn-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-btn-primary-ring mr-2
							${
                config.helperMethodsActionSize === "small"
                  ? " text-xs my-1 mr-1 py-[2px] px-1"
                  : config.helperMethodsActionSize === "large"
                  ? " text-base my-2 mr-2 py-2 px-3"
                  : " text-sm my-2 mr-2 py-1 px-3"
              }
							disabled:bg-gray-300 disabled:text-white
							`}
            >
              <img
                src={"/cvlogo_white.svg"}
                alt="CV Logo"
                className="w-5 h-5 mr-3"
              />
              {config.helpbutton1}
            </button>
          )}

          {config.enableHelpButton2 && (
            <button
              onClick={() => {
                window.open(config.helpbutton2link, "_blank");
              }}
              className={`inline-flex items-center gap-x-1.5 rounded-md bg-btn-primary px-2.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-btn-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-btn-primary-ring mr-2
							${
                config.helperMethodsActionSize === "small"
                  ? " text-xs my-1 mr-1 py-[2px] px-1"
                  : config.helperMethodsActionSize === "large"
                  ? " text-base my-2 mr-2 py-2 px-3"
                  : " text-sm my-2 mr-2 py-1 px-3"
              }
							disabled:bg-gray-300 disabled:text-white
							`}
            >
              <img
                src={"/cvlogo_white.svg"}
                alt="CV Logo"
                className="w-5 h-5 mr-3"
              />
              {config.helpbutton2}
            </button>
          )}

          {config.enableRightFileModalPanel && (
            <DisabledClickWrapper>
              <button
                onClick={() => {
                  if (location.pathname === "/profile") {
                    navigate("/");
                  }
                  setRightFolderPanelOpen(true);
                  cadviewer.cvjs_hideOnlyPop();
                }}
                // folder structure

                className={`inline-flex items-center gap-x-1.5 rounded-md bg-btn-primary px-2.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-btn-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-btn-primary-ring mr-2
							${
                config.helperMethodsActionSize === "small"
                  ? " text-xs my-1 mr-1 py-[2px] px-1"
                  : config.helperMethodsActionSize === "large"
                  ? " text-base my-2 mr-2 py-2 px-3"
                  : " text-sm my-2 mr-2 py-1 px-3"
              }
							disabled:bg-gray-300 disabled:text-white
							`}
                id="folder-structure-button"
                disabled={disableFeature}
                //className={`bg-primary-500 text-white group flex items-center rounded-md px-2 py-2 text-sm  flex-grow-0 flex-shrink-0`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 mr-3"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776"
                  />
                </svg>
                <span>{config.enableRightFileModalPanelText}</span>
              </button>
            </DisabledClickWrapper>
          )}

          {config.enableSpaceInfoCopyButtonTopControl && config.enableSpaceInfoCopyButton && (
            <DisabledClickWrapper>
              <button
                onClick={() => {
                  const newState = !isDataImportActive;
                  setIsDataImportActive(newState);
                  if (!newState) {
                    setCopiedSpaceId(null);
                  }
                  cadviewer.cvjs_hideOnlyPop();
                }}
                className={`inline-flex items-center gap-x-1.5 rounded-md bg-btn-primary px-2.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-btn-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-btn-primary-ring mr-2
							${
                config.helperMethodsActionSize === "small"
                  ? " text-xs my-1 mr-1 py-[2px] px-1"
                  : config.helperMethodsActionSize === "large"
                  ? " text-base my-2 mr-2 py-2 px-3"
                  : " text-sm my-2 mr-2 py-1 px-3"
              }
							disabled:bg-gray-300 disabled:text-white
							${isDataImportActive ? "ring-2 ring-white ring-offset-2 ring-offset-btn-primary" : ""}
							`}
                id="toggle-data-import-button"
                disabled={disableFeature}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 mr-3"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"
                  />
                </svg>
                <span>{config.enableSpaceInfoCopyButtonTopControlText}</span>
              </button>
            </DisabledClickWrapper>
          )}
          {config.enableRightEmployeePanel && (
            <DisabledClickWrapper>
              <button
                onClick={() => {
                  if (location.pathname === "/profile") {
                    navigate("/");
                  }
                  setRightEmployeePanelOpen(true);
                  cadviewer.cvjs_hideOnlyPop();
                }}
                className={`inline-flex items-center gap-x-1.5 rounded-md bg-btn-primary px-2.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-btn-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-btn-primary-ring mr-2
								${
                  config.helperMethodsActionSize === "small"
                    ? " text-xs my-1 mr-1 py-[2px] px-1"
                    : config.helperMethodsActionSize === "large"
                    ? " text-base my-2 mr-2 py-2 px-3"
                    : " text-sm my-2 mr-2 py-1 px-3"
                }
								disabled:bg-gray-300 disabled:text-white
							`}
                disabled={disableFeature}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 mr-3"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
                  />
                </svg>

                <span>{config.enableRightEmployeePanelText}</span>
              </button>
            </DisabledClickWrapper>
          )}
          {config.enableRightArchivePanel && (
            <DisabledClickWrapper>
              <button
                onClick={() => {
                  cadviewer.cvjs_hideOnlyPop();

                  window.alert("Archive Panel is not implemented yet");
                }}
                className={`bg-primary-500 text-white group flex items-center rounded-md px-2 py-2 text-sm  flex-grow-0 flex-shrink-0 disabled:bg-gray-300 disabled:text-white
                  
								${
                  config.helperMethodsActionSize === "small"
                    ? " text-xs my-1 mr-1 py-[2px] px-1"
                    : config.helperMethodsActionSize === "large"
                    ? " text-base my-2 mr-2 py-2 px-3"
                    : " text-sm my-2 mr-2 py-1 px-3"
                }
								disabled:bg-gray-300 disabled:text-white
							`}
                  
                                                
                disabled={disableFeature}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 mr-3"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
                  />
                </svg>
                {config.enableRightArchivePanelText}
              </button>
            </DisabledClickWrapper>
          )}
        </div>
      )}
      {rightMenuMode === "Popup" && (
        <Menu
          as="div"
          className="relative inline-block text-left flex-grow-0 flex-shrink-0"
        >
          <div>
            <Tooltip text={config.rightActionPanelTooltip}>
              <DisabledClickWrapper>
                <MenuButton
                  className={`inline-flex items-center gap-x-1.5 rounded-md bg-btn-primary px-2.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-btn-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-btn-primary-ring mr-2
								${
                  config.helperMethodsActionSize === "small"
                    ? " text-xs my-1 mr-1 py-[2px] px-1"
                    : config.helperMethodsActionSize === "large"
                    ? " text-base my-2 mr-2 py-2 px-3"
                    : " text-sm my-2 mr-2 py-1 px-3"
                }
							disabled:bg-gray-300 disabled:text-white
							`}
                  disabled={disableFeature}
                >
                  {config.enableRightMenuIcon && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-6 h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z"
                      />
                    </svg>
                  )}
                  {config.rightMenuPopupText}
                </MenuButton>
              </DisabledClickWrapper>
            </Tooltip>
          </div>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <MenuItems
              className="absolute z-50 left-0 right-auto md:left-auto md:right-0 mt-2 w-56 origin-top-right divide-y divide-white rounded-md bg-gray-100 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
              style={{ zIndex: 20000 }}
            >
              {config.enableQuickPdfPrint && (
                <div className="px-1 py-1 ">
                  <MenuItem>
                    {({ focus }) => (
                      <DisabledClickWrapper>
                        <button
                          onClick={() => {
                            setQuickPdfPrintOpen(true);
                          }}
                          className={`${
                            focus
                              ? "bg-primary-500 text-white"
                              : "text-gray-900"
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                          disabled={disableFeature}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="size-6"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5Zm-3 0h.008v.008H15V10.5Z"
                            />
                          </svg>
                          <span>{config.enableQuickPdfPrintText}</span>
                        </button>
                      </DisabledClickWrapper>
                    )}
                  </MenuItem>
                </div>
              )}
              {config.enableRightJSONModalPanel && (
                <div className="px-1 py-1 ">
                  <MenuItem>
                    {({ focus }) => (
                      <DisabledClickWrapper>
                        <button
                          onClick={() => {
                            cadviewer.cvjs_hideOnlyPop();
                            setSelectedSpaceObjectID(undefined);
                            cadviewer.cvjs_ClearDrawing(
                              currentCanvasInstance as string
                            );
                            setRightPlansPanelOpen(true);
                          }}
                          className={`${
                            focus
                              ? "bg-primary-500 text-white"
                              : "text-gray-900"
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                          disabled={disableFeature}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-6 h-6 mr-3"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="m7.875 14.25 1.214 1.942a2.25 2.25 0 0 0 1.908 1.058h2.006c.776 0 1.497-.4 1.908-1.058l1.214-1.942M2.41 9h4.636a2.25 2.25 0 0 1 1.872 1.002l.164.246a2.25 2.25 0 0 0 1.872 1.002h2.092a2.25 2.25 0 0 0 1.872-1.002l.164-.246A2.25 2.25 0 0 1 16.954 9h4.636M2.41 9a2.25 2.25 0 0 0-.16.832V12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 12V9.832c0-.287-.055-.57-.16-.832M2.41 9a2.25 2.25 0 0 1 .382-.632l3.285-3.832a2.25 2.25 0 0 1 1.708-.786h8.43c.657 0 1.281.287 1.709.786l3.284 3.832c.163.19.291.404.382.632M4.5 20.25h15A2.25 2.25 0 0 0 21.75 18v-2.625c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125V18a2.25 2.25 0 0 0 2.25 2.25Z"
                            />
                          </svg>
                          <span>{config.enableRightJSONModalPanelText}</span>
                        </button>
                      </DisabledClickWrapper>
                    )}
                  </MenuItem>
                </div>
              )}
              {config.enableHelpButton1 && (
                <div className="px-1 py-1 ">
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        onClick={() => {
                          window.open(config.helpbutton1link, "_blank");
                        }}
                        className={`${
                          focus ? "bg-primary-500 text-white" : "text-gray-900"
                        } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                      >
                        <img
                          src={"/cvlogo_white.svg"}
                          alt="CV Logo"
                          className="w-5 h-5 mr-3"
                        />
                        {config.helpbutton1}
                      </button>
                    )}
                  </MenuItem>
                </div>
              )}
              {config.enableHelpButton2 && (
                <div className="px-1 py-1 ">
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        onClick={() => {
                          window.open(config.helpbutton2link, "_blank");
                        }}
                        className={`${
                          focus ? "bg-primary-500 text-white" : "text-gray-900"
                        } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                      >
                        <img
                          src={"/cvlogo_white.svg"}
                          alt="CV Logo"
                          className="w-5 h-5 mr-3"
                        />
                        {config.helpbutton2}
                      </button>
                    )}
                  </MenuItem>
                </div>
              )}

              {config.enableRightFileModalPanel && (
                <div className="px-1 py-1 ">
                  <MenuItem>
                    {({ focus }) => (
                      <DisabledClickWrapper>
                        <button
                          onClick={() => {
                            setRightFolderPanelOpen(true);
                            cadviewer.cvjs_hideOnlyPop();
                          }}
                          className={`${
                            focus
                              ? "bg-primary-500 text-white"
                              : "text-gray-900"
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                          id="folder-structure-button"
                          disabled={disableFeature}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-6 h-6 mr-3"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776"
                            />
                          </svg>
                          {/* <img
                          src={"/cvlogo_white.svg"}
                          alt="CV Logo"
                          className="w-5 h-5 mr-3"
                        /> */}
                          <span>{config.enableRightFileModalPanelText}</span>
                        </button>
                      </DisabledClickWrapper>
                    )}
                  </MenuItem>
                </div>
              )}

              {config.enableSpaceInfoCopyButtonTopControl && config.enableSpaceInfoCopyButton && (
                <div className="px-1 py-1 ">
                  <MenuItem>
                    {({ focus }) => (
                      <DisabledClickWrapper>
                        <button
                          onClick={() => {
                            const newState = !isDataImportActive;
                            setIsDataImportActive(newState);
                            if (!newState) {
                              setCopiedSpaceId(null);
                            }
                            cadviewer.cvjs_hideOnlyPop();
                          }}
                          className={`${
                            focus || isDataImportActive
                              ? "bg-primary-500 text-white"
                              : "text-gray-900"
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                          id="toggle-data-import-button-menu"
                          disabled={disableFeature}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-6 h-6 mr-3"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"
                            />
                          </svg>
                          <span>{config.enableSpaceInfoCopyButtonTopControlText}</span>
                        </button>
                      </DisabledClickWrapper>
                    )}
                  </MenuItem>
                </div>
              )}
              {config.enableRightEmployeePanel && (
                <div className="px-1 py-1 ">
                  <MenuItem>
                    {({ focus }) => (
                      <DisabledClickWrapper>
                        <button
                          onClick={() => {
                            setRightEmployeePanelOpen(true);
                            cadviewer.cvjs_hideOnlyPop();
                          }}
                          className={`${
                            focus
                              ? "bg-primary-500 text-white"
                              : "text-gray-900"
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                          disabled={disableFeature}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-6 h-6 mr-3"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
                            />
                          </svg>

                          <span>{config.enableRightEmployeePanelText}</span>
                        </button>
                      </DisabledClickWrapper>
                    )}
                  </MenuItem>
                </div>
              )}
              {config.enableRightArchivePanel && (
                <div className="px-1 py-1">
                  <MenuItem>
                    {({ focus }) => (
                      <DisabledClickWrapper>
                        <button
                          onClick={() => {
                            cadviewer.cvjs_hideOnlyPop();
                          }}
                          className={`${
                            focus
                              ? "bg-primary-500 text-white"
                              : "text-gray-900"
                          } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                          disabled={disableFeature}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-6 h-6 mr-3"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
                            />
                          </svg>
                          {config.enableRightArchivePanelText}
                        </button>
                      </DisabledClickWrapper>
                    )}
                  </MenuItem>
                </div>
              )}
            </MenuItems>
          </Transition>
        </Menu>
      )}
      {config.enableRightFileModalPanel && (
        <FolderListRightPanel
          rightFolderPanelOpen={rightFolderPanelOpen}
          setRightFolderPanelOpen={setRightFolderPanelOpen}
        />
      )}
      {config.enableRightJSONModalPanel && (
        <PlanJsonRightPanel
          open={rightPlansPanelOpen}
          setOpen={setRightPlansPanelOpen}
        />
      )}
      {config.enableRightEmployeePanel && (
        <EmployeesListRightPanel
          open={rightEmployeePanelOpen}
          setOpen={setRightEmployeePanelOpen}
        />
      )}
      {config.enableQuickPdfPrint && quickPdfPrintOpen && (
        <QuickPrintPdf
          propertyName={selectedPlan?.propertyName}
          // propertyNumber={selectedPlan?.propertyNumber}
          // plans={selectedPlan?.plans}
          open={quickPdfPrintOpen}
          setOpen={setQuickPdfPrintOpen}
        />
      )}
    </div>
  );
};

export default RightAction;
